<template>
  <NuxtLayout name="standard">
    <template v-if="strapiPageData">
      <ConnectifTag :name="CONNECTIF_PAGE_TAGS.OTHERS" />
      <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.OTHERS" />

      <SHero v-if="heroProps" v-bind="heroProps" />
      <SSection v-if="strapiPageData.attributes.lead" :has-separator="false">
        <SMarkdown :text="strapiPageData.attributes.lead" />
      </SSection>

      <SSection v-if="cardsWide.length" :has-separator="false">
        <LazySCardsWideWrapper :cards="cardsWide" />
      </SSection>

      <LazySCardsImagesWrapper
        v-if="featuredDestinations.items.length"
        v-bind="featuredDestinations"
      />
      <SSection
        v-if="strapiPageData.attributes.seo_text"
        :has-separator="false"
      >
        <TTitle classes="mb-0" tag="h2" size="xs" :centered="true">{{
          strapiPageData.attributes.seo_text.title
        }}</TTitle>

        <SMarkdown :text="strapiPageData.attributes.seo_text.text" />
      </SSection>
      <LazySSection
        v-if="strapiPageData.attributes.sponsors"
        :has-separator="false"
        class="mb-8"
      >
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div
            v-for="(item, index) in strapiPageData.attributes.sponsors"
            :key="index"
          >
            <TLazyimg
              :src="item.image.data.attributes.url"
              :alt="item.alt || ''"
              classes="aspect-w-3 aspect-h-3 object-cover w-full h-48"
            />
          </div>
        </div>
      </LazySSection>
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Types
import { type schemaType as DestinosSchemaType } from '~~/modules/api/strapi/destinos/detail/schema'

// APIs
import getDestinoDetail from '~~/modules/api/strapi/destinos/detail'

// Composables
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import type { propsInterface as HeroProps } from '~~/modules/design-system/sections/s-hero.vue'
import { useToSHero } from '~/composables/transformers/useToSHero' // <-- Importa el transformer
import { useToSCardsWide } from '~/composables/transformers/useToSCardsWide'
import type { SCardsWideProps } from '~~/modules/design-system/sections/s-cards-wide/index.vue'
import { useToSCardsImagesWrapper } from '~/composables/transformers/useToSCardsImagesWrapper'
import type { propsInterface as SCardsImagesWrapperProps } from '~~/modules/design-system/sections/s-cards-images/s-cards-images-wrapper.vue'
import { TLazyimg, TTitle } from '#components'

const route = useRoute()
const { setHeadInfo } = useHeadInfoComposable()

const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error.message,
    fatal: true,
  })
}

const showErrorNotFound = () => {
  throw createError({
    statusCode: 404,
    fatal: true,
  })
}

let strapiPageData: DestinosSchemaType | undefined = undefined
let heroProps: HeroProps | undefined = undefined
let cardsWide: SCardsWideProps[] = []
let featuredDestinations: SCardsImagesWrapperProps = { items: [] }

const rawStrapiPageData = await getDestinoDetail({
  slug: route.params.slug as string,
})

if (
  rawStrapiPageData.success &&
  rawStrapiPageData.success.data?.attributes?.publishedAt
) {
  strapiPageData = rawStrapiPageData.success.data
  setHeadInfo({
    title: strapiPageData.attributes.metadata.title,
    description: strapiPageData.attributes.metadata.description,
    images: [
      {
        url: strapiPageData.attributes.metadata.image.data.attributes.url,
        alt: '',
      },
    ],
    robotsVisible: true,
  })
  heroProps = useToSHero().fromStrapiDestinosDetail(strapiPageData)
  cardsWide = useToSCardsWide().fromStrapiDestinationBanners(strapiPageData)

  if (strapiPageData.attributes.featured_destinations?.length) {
    featuredDestinations =
      useToSCardsImagesWrapper().fromStrapiDestinosDetail(strapiPageData)
  }
} else if (
  rawStrapiPageData.success &&
  !rawStrapiPageData.success.data?.attributes?.publishedAt
) {
  showErrorNotFound()
} else if (rawStrapiPageData.error) {
  showErrorPage(rawStrapiPageData.error as ApiResponseError)
}

sendConnectifPage()
</script>
